import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../../config/constants';
import './TopicList.scss';

/** DB null / JSON undefined 모두 “수치 없음”으로 동일 처리 (백엔드 update는 null·빈값 거부) */
function hasThresholdNumber(el) {
    return el.number != null && el.number !== '';
}

function hasCycleCount(el) {
    return el.cycleCount != null && el.cycleCount !== '';
}

/** COUNT_RESET_ERROR: 횟수(cycle)는 UI에서 비편집 (주기는 서버 cron 고정) */
function isCycleLockedTopic(topic) {
    return topic === 'COUNT_RESET_ERROR';
}

/** 임계값이 있으면 횟수도 편집 진입 가능(횟수만 비어 있을 때 보완) */
function canOpenCycleEdit(el) {
    if (isCycleLockedTopic(el.topic)) return false;
    return hasThresholdNumber(el) || hasCycleCount(el);
}

function getThresholdUnit(topic) {
    if (topic === 'CPU_HIGH') return '%';
    if (topic === 'MEMORY_HIGH') return '%';
    if (topic === 'DISK_USAGE_HIGH') return '%';
    if (topic === 'DISK_FREE_LOW') return 'GB';
    if (topic === 'COUNT_RESET_ERROR') return '회';
    return '';
}

function getThresholdRange(topic) {
    if (topic === 'CPU_HIGH' || topic === 'MEMORY_HIGH') {
        return { min: 0, max: 100 };
    }
    return null;
}

function TopicList(props) {
    const [topics, setTopics] = useState([]);
    /** null | { idx, field: 'number' | 'cycle' } — 임계점·횟수 편집 완전 분리 */
    const [editingCell, setEditingCell] = useState(null);
    const thresholdEditRef = useRef(null);
    const cycleEditRef = useRef(null);

    async function getAlertTopics() {
        const response = await axios.get(`${API_URL}/monitoring-manage/getAlertTopics`, {
            withCredentials: true,
        });
        const { success, alertTopics } = response.data;

        if (success) {
            const normalized = (alertTopics ?? []).map((row) => ({
                ...row,
                number: row.number == null ? null : row.number,
                cycleCount: row.cycleCount == null ? null : row.cycleCount,
            }));
            setTopics(normalized);
        } else {
            console.log('api request failed');
        }
    }

    function getInit() {
        getAlertTopics();
    }

    const handleToggle = async (e, idx) => {
        e.preventDefault();
        const copiedArr = [...topics];

        const response = await axios.post(
            `${API_URL}/monitoring-manage/changeMailingActive`,
            {
                idNo: copiedArr[idx].idNo,
                value: !copiedArr[idx].isMailingActive
            },
            { withCredentials: true }
        );

        const { success } = response.data;

        if (success) {
            copiedArr[idx] = {
                ...copiedArr[idx],
                isMailingActive: !copiedArr[idx].isMailingActive
            };
            setTopics(copiedArr);
        } else {
            console.log('error occured');
        }
    };

    const handleCycleCountChange = (e, idx) => {
        const copiedArr = [...topics];
        copiedArr[idx] = {
            ...copiedArr[idx],
            cycleCount: e.target.value === '' ? '' : Number(e.target.value)
        };
        setTopics(copiedArr);
    };

    const handleNumberChange = (e, idx) => {
        const copiedArr = [...topics];
        copiedArr[idx] = {
            ...copiedArr[idx],
            number: e.target.value === '' ? '' : Number(e.target.value)
        };
        setTopics(copiedArr);
    };

    async function postAlertTopicUpdate(target, num, cycle) {
        const response = await axios.post(
            `${API_URL}/monitoring-manage/updateAlertTopicNumber`,
            {
                idNo: target.idNo,
                number: num,
                cycleCount: cycle,
            },
            { withCredentials: true }
        );
        return response.data;
    }

    const handleSaveNumberOnly = async (e, idx) => {
        e.preventDefault();
        const target = topics[idx];
        const raw = target.number;
        const num = Number(raw);
        if (!Number.isFinite(num)) {
            alert('유효한 숫자를 입력하세요.');
            return;
        }
        const range = getThresholdRange(target.topic);
        if (range && (num < range.min || num > range.max)) {
            alert(`${range.min}~${range.max} 사이의 값을 입력하세요.`);
            return;
        }

        let cycle;
        if (isCycleLockedTopic(target.topic)) {
            const c = Number(target.cycleCount);
            cycle = Number.isFinite(c) && c >= 1 && Number.isInteger(c) ? c : 1;
        } else {
            const rawCycle = target.cycleCount;
            if (rawCycle === '' || rawCycle == null || rawCycle === undefined) {
                alert('저장하려면 횟수 값이 필요합니다. 횟수 열에서 먼저 설정해 주세요.');
                return;
            }
            cycle = Number(rawCycle);
            if (!Number.isFinite(cycle) || cycle < 1 || !Number.isInteger(cycle)) {
                alert('횟수 열에서 유효한 반복 횟수(1 이상 정수)를 설정한 뒤 다시 시도하세요.');
                return;
            }
        }

        const { success, error } = await postAlertTopicUpdate(target, num, cycle);
        if (!success) {
            if (error === 'invalid_params') {
                alert('수치를 입력하세요.');
            } else {
                console.log('alert topic update failed', error);
            }
            return;
        }
        setEditingCell(null);
        getAlertTopics();
    };

    const handleSaveCycleOnly = async (e, idx) => {
        e.preventDefault();
        const target = topics[idx];
        if (isCycleLockedTopic(target.topic)) return;
        const raw = target.number;
        const num = Number(raw);
        if (!Number.isFinite(num)) {
            alert('임계값이 없습니다. 임계점 열에서 먼저 설정해 주세요.');
            return;
        }
        const range = getThresholdRange(target.topic);
        if (range && (num < range.min || num > range.max)) {
            alert(`${range.min}~${range.max} 사이의 임계값이어야 합니다. 임계점에서 수정해 주세요.`);
            return;
        }

        const rawCycle = target.cycleCount;
        if (rawCycle === '' || rawCycle == null || rawCycle === undefined) {
            alert('반복 횟수를 입력하세요.');
            return;
        }
        const cycle = Number(rawCycle);
        if (!Number.isFinite(cycle) || cycle < 1 || !Number.isInteger(cycle)) {
            alert('반복 횟수는 1 이상의 정수를 입력하세요.');
            return;
        }

        const { success, error } = await postAlertTopicUpdate(target, num, cycle);
        if (!success) {
            if (error === 'invalid_params') {
                alert('수치를 입력하세요.');
            } else {
                console.log('alert topic update failed', error);
            }
            return;
        }
        setEditingCell(null);
        getAlertTopics();
    };

    const openCellEdit = async (idx, field) => {
        if (field === 'cycle' && isCycleLockedTopic(topics[idx]?.topic)) return;
        const prev = editingCell;
        if (
            prev !== null &&
            (prev.idx !== idx || prev.field !== field)
        ) {
            await getAlertTopics();
        }
        setEditingCell({ idx, field });
    };

    useEffect(() => {
        getInit();
    }, []);

    useEffect(() => {
        if (editingCell === null) return undefined;

        function onDocPointerDown(e) {
            if (thresholdEditRef.current?.contains(e.target)) return;
            if (cycleEditRef.current?.contains(e.target)) return;
            setEditingCell(null);
            getAlertTopics();
        }

        document.addEventListener('pointerdown', onDocPointerDown);
        return () => document.removeEventListener('pointerdown', onDocPointerDown);
    }, [editingCell]);

    return (
        <div className='topicList'>
            <h3>토픽 별 메일링 설정</h3>
            <table>
                <thead>
                    <tr>
                        <th className='topic'>토픽</th>
                        <th className="description">설명</th>
                        <th className='isMailingActive'>메일링</th>
                        <th className="numberchange">임계점</th>
                        <th className="cycleCount">횟수</th>
                    </tr>
                </thead>
                <tbody>
                    {topics.map((el, idx) => {
                        const isOn = el.isMailingActive;
                        const isEditingNumber =
                            editingCell?.idx === idx && editingCell?.field === 'number';
                        const isEditingCycle =
                            editingCell?.idx === idx && editingCell?.field === 'cycle';
                        const thresholdRange = getThresholdRange(el.topic);

                        const thresholdDisplayText = hasThresholdNumber(el)
                            ? `${el.number}${
                                  getThresholdUnit(el.topic)
                                      ? ` ${getThresholdUnit(el.topic)}`
                                      : ''
                              }`
                            : '-';

                        const cycleDisplayText = hasCycleCount(el) ? `${el.cycleCount}회` : '-';

                        return (
                            <tr key={`topic_${idx}`}>
                                <td>{el.topic}</td>
                                <td>{el.description ?? '-'}</td>
                                <td>
                                    <button
                                        onClick={e => handleToggle(e, idx)}
                                        style={{
                                            padding: "5px 10px",
                                            border: "none",
                                            borderRadius: "20px",
                                            backgroundColor: isOn ? "#4caf50" : "#ccc",
                                            color: "white",
                                            cursor: "pointer",
                                            fontSize: "12px",
                                        }}
                                    >
                                        {isOn ? "ON" : "OFF"}
                                    </button>
                                </td>
                                <td className="topicList__thresholdCell">
                                    {isEditingNumber ? (
                                        <span
                                            ref={thresholdEditRef}
                                            className="topicList__thresholdEdit topicList__thresholdEdit--numberOnly"
                                        >
                                            <input
                                                className="topicList__numberInput"
                                                type="number"
                                                min={thresholdRange?.min}
                                                max={thresholdRange?.max}
                                                step="1"
                                                value={
                                                    el.number === '' || el.number == null
                                                        ? ''
                                                        : el.number
                                                }
                                                onChange={(e) => handleNumberChange(e, idx)}
                                                autoFocus
                                            />
                                            <button
                                                type="button"
                                                className="topicList__thresholdBtn topicList__thresholdBtn--save"
                                                onClick={(e) => handleSaveNumberOnly(e, idx)}
                                            >
                                                수정
                                            </button>
                                        </span>
                                    ) : isEditingCycle ? (
                                        <span className="topicList__valueStatic">
                                            {thresholdDisplayText}
                                        </span>
                                    ) : hasThresholdNumber(el) ? (
                                        <button
                                            type="button"
                                            className="topicList__numberDisplay"
                                            onClick={() => openCellEdit(idx, 'number')}
                                        >
                                            {el.number}
                                            {getThresholdUnit(el.topic) && ` ${getThresholdUnit(el.topic)}`}
                                        </button>
                                    ) : (
                                        '-'
                                    )}
                                </td>
                                <td className="topicList__cycleCell">
                                    {isCycleLockedTopic(el.topic) ? (
                                        <span className="topicList__valueStatic">-</span>
                                    ) : isEditingCycle ? (
                                        <span
                                            ref={cycleEditRef}
                                            className="topicList__thresholdEdit topicList__thresholdEdit--cycleCol"
                                        >
                                            <input
                                                className="topicList__numberInput topicList__cycleInput"
                                                type="number"
                                                min={1}
                                                step="1"
                                                value={
                                                    el.cycleCount === '' || el.cycleCount == null
                                                        ? ''
                                                        : el.cycleCount
                                                }
                                                onChange={(e) => handleCycleCountChange(e, idx)}
                                                autoFocus
                                            />
                                            <button
                                                type="button"
                                                className="topicList__thresholdBtn topicList__thresholdBtn--save"
                                                onClick={(e) => handleSaveCycleOnly(e, idx)}
                                            >
                                                수정
                                            </button>
                                        </span>
                                    ) : isEditingNumber ? (
                                        <span className="topicList__valueStatic">
                                            {cycleDisplayText}
                                        </span>
                                    ) : canOpenCycleEdit(el) ? (
                                        <button
                                            type="button"
                                            className="topicList__numberDisplay"
                                            onClick={() => openCellEdit(idx, 'cycle')}
                                        >
                                            {cycleDisplayText}
                                        </button>
                                    ) : (
                                        '-'
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default TopicList;
